/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useSearchParams } from "react-router-dom";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { useCustomerAuth, describeAxiosError, formatApiError } from "@/contexts/CustomerAuthContext";
import AuthShell, { Field } from "@/components/customer/AuthShell";
import SEO from "@/components/site/SEO";

const COUNTRIES = [
  ["GB","United Kingdom"],["NG","Nigeria"],["GH","Ghana"],["KE","Kenya"],["UG","Uganda"],
  ["ZA","South Africa"],["US","United States"],["CA","Canada"],["FR","France"],["DE","Germany"],
];

export default function Register() {
  const { user, register } = useCustomerAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [f, setF] = useState({ email:"", password:"", name:"", country:"GB", marketing_opt_in:false, ref_code:"" });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  // Capture referral code from URL (?ref=ABC123) on first paint
  useEffect(() => {
    const ref = params.get("ref");
    if (ref) setF((prev) => ({ ...prev, ref_code: ref.toUpperCase().slice(0, 16) }));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (user && user !== false) return <Navigate to="/app" replace />;

  const update = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setF({ ...f, [k]: v });
    setErrors({ ...errors, [k]: null });
  };

  const localValidate = () => {
    const e = {};
    if (!f.name.trim()) e.name = "Required";
    if (!f.email.trim()) e.email = "Required";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(f.email)) e.email = "Enter a valid email";
    if (f.password.length < 12) e.password = "At least 12 characters";
    else if (!(/[a-z]/.test(f.password) && /[A-Z]/.test(f.password) && /\d/.test(f.password)))
      e.password = "Must include uppercase, lowercase and a number";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!localValidate()) return;
    setSubmitting(true);
    try {
      await register({ ...f, email: f.email.trim().toLowerCase() });
      setSuccess(true);
    } catch (err) {
      const msg = describeAxiosError(err, "Sign-up failed.");
      toast.error(msg);
      setErrors({ ...errors, _form: msg });
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <AuthShell eyebrow="Almost there" title="Check your inbox"
        subtitle={`We&rsquo;ve sent a verification link to ${f.email}. It expires in 24 hours.`}>
        <SEO title="Verify your email" path="/register" />
        <div className="rounded-xl border border-brand-line bg-brand-bgSoft p-5 text-sm text-brand-ink leading-relaxed">
          Click the link in the email to confirm your account. You won&rsquo;t be able to sign in until your email is verified.
        </div>
        <Link to="/login" className="mt-6 block text-sm font-semibold text-brand-green hover:text-brand-greenHover">
          Back to sign in →
        </Link>
      </AuthShell>
    );
  }

  return (
    <AuthShell eyebrow="Create an account" title="Join OneDial Africa"
      subtitle="It takes less than a minute. No card required."
      footer={<>Already have an account? <Link to="/login" className="font-semibold text-brand-green hover:text-brand-greenHover">Sign in</Link></>}>
      <SEO title="Create your account" path="/register" />
      <form onSubmit={onSubmit} className="space-y-5" data-testid="register-form" noValidate>
        <Field label="Full name" testId="reg-name" value={f.name} onChange={update("name")}
               placeholder="Amina Okafor" required autoComplete="name" error={errors.name} name="name" />
        <Field label="Email" type="email" testId="reg-email" value={f.email} onChange={update("email")}
               placeholder="you@example.com" required autoComplete="email" error={errors.email} name="email" />
        <Field label="Password" type="password" testId="reg-password" value={f.password} onChange={update("password")}
               placeholder="Min 12 chars · upper, lower, number" required autoComplete="new-password"
               error={errors.password} name="password" />
        <div>
          <label className="block text-xs uppercase tracking-[0.22em] font-semibold mb-2">Country <span className="text-brand-green">*</span></label>
          <select value={f.country} onChange={update("country")} data-testid="reg-country"
                  className="w-full rounded-md border border-brand-line bg-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green/40 focus:border-brand-green">
            {COUNTRIES.map(([c, n]) => <option key={c} value={c}>{n}</option>)}
          </select>
        </div>
        <label className="flex items-start gap-3 text-sm text-brand-muted">
          <input type="checkbox" checked={f.marketing_opt_in} onChange={update("marketing_opt_in")}
                 data-testid="reg-marketing" className="mt-1 h-4 w-4 rounded border-brand-line accent-brand-green" />
          <span>I'd like to receive product updates and offers from OneDial Africa.</span>
        </label>
        <p className="text-xs text-brand-muted">
          By creating an account you agree to our{" "}
          <Link to="/terms-and-conditions" className="underline hover:text-brand-ink">Terms</Link> and{" "}
          <Link to="/privacy-policy" className="underline hover:text-brand-ink">Privacy Policy</Link>.
        </p>
        <button type="submit" disabled={submitting} data-testid="reg-submit"
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green px-6 py-3 text-sm font-semibold text-brand-ink hover:bg-brand-greenHover disabled:opacity-70">
          {submitting ? <><Loader2 className="h-4 w-4 animate-spin" /> Creating account…</> : "Create account"}
        </button>
      </form>
    </AuthShell>
  );
}
